import { useEffect, useRef } from 'react'
import { Bot, Globe, User, Building2, AlertCircle, RefreshCw } from 'lucide-react'
import { ChatMessage } from './ChatMessage'
import { usePretext, useContainerWidth } from '../../hooks/usePretext'

interface MessageAttachment {
  name: string
  type: string
  size: number
  previewUrl?: string
}

interface Message {
  id?: string
  role: 'user' | 'assistant' | 'system'
  content: string
  tool_calls?: any[]
  timestamp?: string
  attachments?: MessageAttachment[]
}

interface SelectedScope {
  scope: string
  entityType?: string
  entityId?: string
  entityName?: string
}

interface Props {
  messages: Message[]
  streamingMessage: string | null
  toolStatus: string | null
  isLoadingMessages: boolean
  error: string | null
  onRetry: () => void
  onSendSuggestion: (msg: string) => void
  selectedScope: SelectedScope
  onShowScopeSelector: () => void
}

const SUGGESTIONS = [
  "What's new in GitLab 17?",
  'GitLab vs GitHub for DoD',
  'IL4 compliance requirements',
  'HA deployment architecture',
]

export function ChatMessages({
  messages,
  streamingMessage,
  toolStatus,
  isLoadingMessages,
  error,
  onRetry,
  onSendSuggestion,
  selectedScope,
  onShowScopeSelector,
}: Props) {
  const endRef = useRef<HTMLDivElement>(null)

  // Pretext: measure streaming message height without DOM reflow.
  // containerRef tracks the message list width; usePretext pre-computes
  // the streaming bubble height so the scroll anchor doesn't jump.
  const [containerRef, containerWidth] = useContainerWidth<HTMLDivElement>()
  const { height: streamHeight } = usePretext(
    streamingMessage ?? '',
    // Leave room for avatar (28px) + gap (12px) + padding (32px)
    containerWidth !== null ? containerWidth - 72 : null,
    { lineHeight: 22 },
  )

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, streamingMessage])

  /* ---------- Loading skeleton ---------- */
  if (isLoadingMessages) {
    return (
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {/* Skeleton user bubble */}
        <div className="flex justify-end">
          <div className="w-[65%] h-10 skeleton rounded-2xl rounded-br-md" />
        </div>
        {/* Skeleton assistant bubble */}
        <div className="flex gap-3">
          <div className="w-7 h-7 skeleton rounded-lg flex-shrink-0" />
          <div className="flex-1 space-y-2">
            <div className="h-4 skeleton rounded w-full" />
            <div className="h-4 skeleton rounded w-[90%]" />
            <div className="h-4 skeleton rounded w-[75%]" />
          </div>
        </div>
        {/* Skeleton user bubble */}
        <div className="flex justify-end">
          <div className="w-[50%] h-10 skeleton rounded-2xl rounded-br-md" />
        </div>
        {/* Skeleton assistant bubble */}
        <div className="flex gap-3">
          <div className="w-7 h-7 skeleton rounded-lg flex-shrink-0" />
          <div className="flex-1 space-y-2">
            <div className="h-4 skeleton rounded w-full" />
            <div className="h-4 skeleton rounded w-[85%]" />
            <div className="h-4 skeleton rounded w-[60%]" />
            <div className="h-20 skeleton rounded-lg w-full" />
            <div className="h-4 skeleton rounded w-[70%]" />
          </div>
        </div>

        <style>{`
          @keyframes shimmer {
            0% { background-position: -200px 0; }
            100% { background-position: 200px 0; }
          }
          .skeleton {
            background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%);
            background-size: 200px 100%;
            animation: shimmer 1.5s infinite;
          }
        `}</style>
      </div>
    )
  }

  /* ---------- Empty state ---------- */
  const showEmpty = messages.length === 0 && !streamingMessage

  if (showEmpty) {
    const isGeneralSelected = selectedScope.scope === 'general'
    const hasEntity = !!selectedScope.entityId

    const entityIcon =
      selectedScope.entityType === 'customer' ? Building2 : User

    return (
      <div className="flex-1 overflow-y-auto px-4 py-4">
        <div className="flex flex-col items-center justify-center h-full text-center">
          {/* Bot icon */}
          <div className="w-16 h-16 bg-primary-50 rounded-2xl flex items-center justify-center mb-4">
            <Bot className="w-9 h-9 text-primary-300" />
          </div>

          <h3 className="text-base font-semibold text-gray-900 mb-1">
            GitLab DoD Expert
          </h3>
          <p className="text-sm text-gray-500 mb-5 max-w-[280px]">
            Ask about GitLab deployments, compliance, features, or your current
            lead context.
          </p>

          {/* Scope selector */}
          <div className="w-full max-w-[360px] mb-6">
            <div className="text-[11px] font-medium text-gray-500 uppercase tracking-wide mb-2">
              Chat scope
            </div>
            <div className="space-y-1.5">
              {/* General option */}
              <button
                onClick={() => onShowScopeSelector()}
                className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-lg transition-all text-left group ${
                  isGeneralSelected
                    ? 'border-2 border-primary-500 bg-primary-50/50'
                    : 'border border-gray-200 hover:border-primary-300 hover:bg-primary-50/50'
                }`}
              >
                <div
                  className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
                    isGeneralSelected
                      ? 'bg-primary-100'
                      : 'bg-gray-100 group-hover:bg-primary-100'
                  }`}
                >
                  <Globe
                    className={`w-4 h-4 transition-colors ${
                      isGeneralSelected
                        ? 'text-primary-600'
                        : 'text-gray-500 group-hover:text-primary-600'
                    }`}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900">
                    General
                  </div>
                  <div className="text-[11px] text-gray-400">
                    GitLab, compliance, architecture — no specific entity
                  </div>
                </div>
                <div
                  className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
                    isGeneralSelected
                      ? 'border-primary-500'
                      : 'border-gray-300'
                  }`}
                >
                  {isGeneralSelected && (
                    <div className="w-2 h-2 rounded-full bg-primary-500" />
                  )}
                </div>
              </button>

              {/* Current page context option */}
              {hasEntity && (
                <button
                  onClick={() => onShowScopeSelector()}
                  className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-lg transition-all text-left group ${
                    !isGeneralSelected
                      ? 'border-2 border-primary-500 bg-primary-50/50'
                      : 'border border-gray-200 hover:border-primary-300 hover:bg-primary-50/50'
                  }`}
                >
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
                      !isGeneralSelected
                        ? 'bg-primary-100'
                        : 'bg-gray-100 group-hover:bg-primary-100'
                    }`}
                  >
                    {(() => {
                      const Icon = entityIcon
                      return (
                        <Icon
                          className={`w-4 h-4 transition-colors ${
                            !isGeneralSelected
                              ? 'text-primary-600'
                              : 'text-gray-500 group-hover:text-primary-600'
                          }`}
                        />
                      )
                    })()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                      {selectedScope.entityName || 'Current context'}
                      <span className="inline-flex items-center px-1.5 py-0 bg-primary-100 text-primary-600 rounded text-[10px] font-medium">
                        Current page
                      </span>
                    </div>
                    <div className="text-[11px] text-gray-400">
                      Scoped to this {selectedScope.entityType || 'entity'}
                    </div>
                  </div>
                  <div
                    className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
                      !isGeneralSelected
                        ? 'border-primary-500'
                        : 'border-gray-300'
                    }`}
                  >
                    {!isGeneralSelected && (
                      <div className="w-2 h-2 rounded-full bg-primary-500" />
                    )}
                  </div>
                </button>
              )}
            </div>
          </div>

          {/* Suggestion chips */}
          <div className="flex flex-wrap justify-center gap-2 max-w-[340px]">
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                onClick={() => onSendSuggestion(s)}
                className="px-3 py-1.5 bg-primary-50 hover:bg-primary-100 text-primary-700 rounded-full text-xs font-medium transition-colors"
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      </div>
    )
  }

  /* ---------- Conversation view ---------- */
  return (
    <div ref={containerRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
      {/* Error banner */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
            <span className="text-xs text-red-700">{error}</span>
          </div>
          <button
            onClick={onRetry}
            className="text-xs font-medium text-red-600 hover:text-red-700 flex items-center gap-1"
          >
            <RefreshCw className="w-3 h-3" /> Retry
          </button>
        </div>
      )}

      {messages.map((msg, i) => (
        <ChatMessage
          key={msg.id || i}
          role={msg.role}
          content={msg.content}
          toolCalls={
            msg.tool_calls
              ? typeof msg.tool_calls === 'string'
                ? JSON.parse(msg.tool_calls)
                : msg.tool_calls
              : undefined
          }
          timestamp={msg.timestamp}
          attachments={msg.attachments}
        />
      ))}

      {/* Tool status pill */}
      {toolStatus && (
        <div className="flex items-center gap-2 ml-10">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 text-amber-600 rounded-full text-[11px] font-medium">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
            {toolStatus === 'web_search'
              ? 'Searching the web...'
              : toolStatus === 'knowledge_search'
                ? 'Searching knowledge base...'
                : `Running ${toolStatus}...`}
          </div>
        </div>
      )}

      {streamingMessage && (
        // min-height hint from Pretext prevents layout shift as tokens arrive
        <div style={streamHeight ? { minHeight: streamHeight } : undefined}>
          <ChatMessage role="assistant" content={streamingMessage} isStreaming />
        </div>
      )}

      <div ref={endRef} />
    </div>
  )
}
